/**
 * Gridspot is an image question with a designer grid overlay.
 * Designer overlay cells are 1-based: rows 1..N (top → bottom), columns 1..N (left → right).
 * Tap mode is controlled by gridInputType: radio (single-tap, auto-advance) or
 * multiselect (checkbox-like, advance on Next). Answers are always ObjectId[].
 * Options support hide_questions (same as radio/checkbox). Other radio-style
 * flags (finish_survey, route_to_index, write_in, etc.) are not used on spots.
 */

export const GRIDSPOT_QUESTION_TYPE = "gridspot";

export function isGridspotQuestion(questionOrType) {
  const type =
    typeof questionOrType === "string"
      ? questionOrType
      : questionOrType?.questionType;
  return type === GRIDSPOT_QUESTION_TYPE;
}

/** True when Gridspot should behave like a checkbox (multi-tap, Next to continue). */
export function isGridspotMultiSelect(question) {
  return (
    isGridspotQuestion(question) && question?.gridInputType === "multiselect"
  );
}

export function getGridspotRows(question) {
  const rows = Number(question?.gridspot_rows ?? question?.gridspot?.rows);
  return Number.isInteger(rows) && rows > 0 ? rows : 0;
}

export function getGridspotColumns(question) {
  const columns = Number(
    question?.gridspot_columns ?? question?.gridspot?.columns,
  );
  return Number.isInteger(columns) && columns > 0 ? columns : 0;
}

/**
 * Every cell a gridspot covers. A spot may span several cells, scattered or not;
 * they share one answer and one value. Falls back to the anchor
 * (gridspot_row / gridspot_column) when gridspot_cells is absent, so spots authored
 * before multi-cell keep resolving. Mirrors getGridspotCells in sf-api.
 * @returns {{row: number, column: number}[]}
 */
export function getGridspotCells(option) {
  // Number(null) and Number("") are 0, not NaN, so an unset coordinate has to be
  // rejected before the cast or a missing anchor would read as cell (0, 0).
  const toCell = (row, column) => {
    const r = row == null || row === "" ? NaN : Number(row);
    const c = column == null || column === "" ? NaN : Number(column);
    return Number.isInteger(r) && Number.isInteger(c)
      ? { row: r, column: c }
      : null;
  };

  const cells = Array.isArray(option?.gridspot_cells)
    ? option.gridspot_cells
    : [];
  if (cells.length) {
    return cells.map((cell) => toCell(cell?.row, cell?.column)).filter(Boolean);
  }
  const anchor = toCell(option?.gridspot_row, option?.gridspot_column);
  return anchor ? [anchor] : [];
}

export function getGridspotImageUrl(question) {
  const nestedImage = question?.gridspot?.image;
  const nestedUrl =
    typeof nestedImage === "string" && nestedImage
      ? nestedImage
      : question?.gridspot?.imageUrl;

  const flatImage =
    typeof question?.gridspot_image === "string" ? question.gridspot_image : "";

  return (
    question?.gridspot_image_url ||
    question?.gridspotImageUrl ||
    flatImage ||
    nestedUrl ||
    ""
  );
}

/**
 * Map a pointer position inside the rendered image to a 1-based grid cell.
 * @returns {{ row: number, column: number } | null}
 */
export function getGridspotCellFromPoint({
  x,
  y,
  width,
  height,
  rows,
  columns,
}) {
  if (width <= 0 || height <= 0 || rows < 1 || columns < 1) {
    return null;
  }
  if (x < 0 || y < 0 || x > width || y > height) {
    return null;
  }

  const column = Math.min(
    columns,
    Math.max(1, Math.floor((x / width) * columns) + 1),
  );
  const row = Math.min(rows, Math.max(1, Math.floor((y / height) * rows) + 1));
  return { row, column };
}

export function findGridspotOption(options, cell, rows, columns) {
  if (!cell || !Array.isArray(options) || !options.length) {
    return null;
  }

  const matches = options.filter(
    (option) =>
      isOptionOnGrid(option, rows, columns) &&
      getGridspotCells(option).some(
        ({ row, column }) => row === cell.row && column === cell.column,
      ),
  );

  return matches.length === 1 ? matches[0] : null;
}

export function getGridspotCellStyle(row, column, rows, columns) {
  const normalizedRow = Number(row);
  const normalizedColumn = Number(column);

  if (
    rows < 1 ||
    columns < 1 ||
    !Number.isInteger(normalizedRow) ||
    !Number.isInteger(normalizedColumn) ||
    normalizedRow < 1 ||
    normalizedRow > rows ||
    normalizedColumn < 1 ||
    normalizedColumn > columns
  ) {
    return null;
  }

  return {
    left: `${((normalizedColumn - 1) / columns) * 100}%`,
    top: `${((normalizedRow - 1) / rows) * 100}%`,
    width: `${100 / columns}%`,
    height: `${100 / rows}%`,
  };
}

export function buildGridspotOutlinePath(cells, rows, columns) {
  if (!Array.isArray(cells) || !cells.length || rows < 1 || columns < 1) {
    return null;
  }

  const selected = new Set();
  for (let i = 0; i < cells.length; i += 1) {
    const row = Number(cells[i]?.row);
    const column = Number(cells[i]?.column);
    if (
      Number.isInteger(row) &&
      Number.isInteger(column) &&
      row >= 1 &&
      row <= rows &&
      column >= 1 &&
      column <= columns
    ) {
      selected.add(`${row}-${column}`);
    }
  }
  if (!selected.size) return null;

  const has = (row, column) => selected.has(`${row}-${column}`);
  const left = (column) => ((column - 1) / columns) * 100;
  const right = (column) => (column / columns) * 100;
  const top = (row) => ((row - 1) / rows) * 100;
  const bottom = (row) => (row / rows) * 100;

  // Directed perimeter edges (clockwise around each cell's exterior).
  const edges = [];
  selected.forEach((key) => {
    const [row, column] = key.split("-").map(Number);
    if (!has(row - 1, column)) {
      edges.push([left(column), top(row), right(column), top(row)]);
    }
    if (!has(row, column + 1)) {
      edges.push([right(column), top(row), right(column), bottom(row)]);
    }
    if (!has(row + 1, column)) {
      edges.push([right(column), bottom(row), left(column), bottom(row)]);
    }
    if (!has(row, column - 1)) {
      edges.push([left(column), bottom(row), left(column), top(row)]);
    }
  });

  if (!edges.length) return null;

  const pointKey = (x, y) => `${x.toFixed(4)},${y.toFixed(4)}`;
  const byStart = new Map();
  edges.forEach((edge, index) => {
    const key = pointKey(edge[0], edge[1]);
    if (!byStart.has(key)) byStart.set(key, []);
    byStart.get(key).push(index);
  });

  const used = new Set();
  const parts = [];

  for (let startIndex = 0; startIndex < edges.length; startIndex += 1) {
    if (used.has(startIndex)) continue;
    let index = startIndex;
    const [sx, sy] = edges[index];
    let d = `M ${sx} ${sy}`;
    let guard = 0;
    while (index != null && !used.has(index) && guard < edges.length + 1) {
      used.add(index);
      const [, , ex, ey] = edges[index];
      d += ` L ${ex} ${ey}`;
      const nextList = byStart.get(pointKey(ex, ey)) || [];
      index = null;
      for (let n = 0; n < nextList.length; n += 1) {
        if (!used.has(nextList[n])) {
          index = nextList[n];
          break;
        }
      }
      guard += 1;
    }
    d += " Z";
    parts.push(d);
  }

  return parts.join(" ");
}

export function buildGridspotCutoutMaskPath(cells, rows, columns) {
  if (!Array.isArray(cells) || !cells.length || rows < 1 || columns < 1) {
    return null;
  }

  const holes = [];
  const seen = new Set();
  for (let i = 0; i < cells.length; i += 1) {
    const row = Number(cells[i]?.row);
    const column = Number(cells[i]?.column);
    if (
      !Number.isInteger(row) ||
      !Number.isInteger(column) ||
      row < 1 ||
      row > rows ||
      column < 1 ||
      column > columns
    ) {
      continue;
    }
    const key = `${row}-${column}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const left = ((column - 1) / columns) * 100;
    const right = (column / columns) * 100;
    const top = ((row - 1) / rows) * 100;
    const bottom = (row / rows) * 100;
    holes.push(`M ${left} ${top} H ${right} V ${bottom} H ${left} Z`);
  }

  if (!holes.length) return null;
  return `M 0 0 H 100 V 100 H 0 Z ${holes.join(" ")}`;
}

export function findGridspotOptionById(options, optionId) {
  if (optionId == null || optionId === "" || !Array.isArray(options)) {
    return null;
  }
  return (
    options.find((option) => String(option?._id) === String(optionId)) || null
  );
}

function unwrapGridspotOptionId(optionId) {
  let id = optionId;
  while (Array.isArray(id)) {
    id = id.length ? id[0] : null;
  }
  if (id == null || id === "") {
    return null;
  }
  return id;
}

/**
 * Normalise a Gridspot answer to ObjectId[].
 * Accepts a single id, an id array (multi-select), or nested arrays from older callers.
 */
export function toGridspotAnswerValue(optionIdOrIds) {
  if (Array.isArray(optionIdOrIds)) {
    const ids = [];
    const seen = new Set();
    optionIdOrIds.forEach((item) => {
      const id = unwrapGridspotOptionId(item);
      if (id == null) return;
      const key = String(id);
      if (seen.has(key)) return;
      seen.add(key);
      ids.push(id);
    });
    return ids;
  }
  const id = unwrapGridspotOptionId(optionIdOrIds);
  return id == null ? [] : [id];
}

/** All selected option ids from a Gridspot answer (radio or multiselect). */
export function readGridspotSelectedOptionIds(answerValue) {
  return toGridspotAnswerValue(answerValue);
}

/** First selected option id (radio / legacy callers). */
export function readGridspotSelectedOptionId(answerValue) {
  const ids = readGridspotSelectedOptionIds(answerValue);
  return ids.length ? ids[0] : undefined;
}

/** Toggle an option id in a Gridspot multiselect answer. */
export function toggleGridspotAnswerValue(currentValue, optionId) {
  const id = unwrapGridspotOptionId(optionId);
  if (id == null) {
    return readGridspotSelectedOptionIds(currentValue);
  }
  const ids = readGridspotSelectedOptionIds(currentValue);
  const key = String(id);
  if (ids.some((existing) => String(existing) === key)) {
    return ids.filter((existing) => String(existing) !== key);
  }
  return [...ids, id];
}

function getOptionCellKeys(option) {
  return getGridspotCells(option).map(({ row, column }) => `${row}-${column}`);
}

function isOptionOnGrid(option, rows, columns) {
  const cells = getGridspotCells(option);
  return (
    cells.length > 0 &&
    cells.every(
      ({ row, column }) =>
        row >= 1 && row <= rows && column >= 1 && column <= columns,
    )
  );
}

/**
 * Returns a configuration error code when the gridspot cannot be answered,
 * or null when rows/columns, image URL, and at least one mappable option exist.
 */
export function getGridspotConfigurationError(question) {
  const rows = getGridspotRows(question);
  const columns = getGridspotColumns(question);
  if (!rows || !columns) {
    return "invalid-grid";
  }

  const imageUrl = getGridspotImageUrl(question);
  if (!imageUrl || !String(imageUrl).trim()) {
    return "missing-image";
  }

  const options = question?.options || [];
  if (!options.length) {
    return "no-options";
  }

  const cellKeys = new Set();
  for (const option of options) {
    if (!isOptionOnGrid(option, rows, columns)) {
      return "invalid-options";
    }

    // Two gridspots sharing a cell make a tap ambiguous, so reject it the same way
    // the API does — now across every cell each spot covers.
    for (const cellKey of getOptionCellKeys(option)) {
      if (cellKeys.has(cellKey)) {
        return "duplicate-cells";
      }
      cellKeys.add(cellKey);
    }
  }

  return null;
}

export function isGridspotConfigured(question) {
  return getGridspotConfigurationError(question) == null;
}

const GRIDSPOT_TEXT_ALIGN = {
  "top-left": {
    alignItems: "flex-start",
    justifyContent: "flex-start",
    textAlign: "left",
  },
  "top-center": {
    alignItems: "flex-start",
    justifyContent: "center",
    textAlign: "center",
  },
  "top-right": {
    alignItems: "flex-start",
    justifyContent: "flex-end",
    textAlign: "right",
  },
  "center-left": {
    alignItems: "center",
    justifyContent: "flex-start",
    textAlign: "left",
  },
  "center-center": {
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  "center-right": {
    alignItems: "center",
    justifyContent: "flex-end",
    textAlign: "right",
  },
  "bottom-left": {
    alignItems: "flex-end",
    justifyContent: "flex-start",
    textAlign: "left",
  },
  "bottom-center": {
    alignItems: "flex-end",
    justifyContent: "center",
    textAlign: "center",
  },
  "bottom-right": {
    alignItems: "flex-end",
    justifyContent: "flex-end",
    textAlign: "right",
  },
};

const GRIDSPOT_TEXT_STYLE_KEYS = [
  "font",
  "size",
  "colour",
  "background",
  "background_opacity",
  "position",
];

function isGridspotTextOverlayEnabled(question) {
  const flag = question?.gridspot_text_overlay;
  return flag === true || flag === "true" || flag === 1;
}

function stripHtml(value) {
  return String(value)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function selectedLocale() {
  try {
    return localStorage.getItem("locale_lang") || "en";
  } catch {
    return "en";
  }
}

function matchesLocale(language, locale) {
  const lang = String(language || "").toLowerCase();
  const selected = String(locale || "en").toLowerCase();
  const selectedBase = selected.split("-")[0];
  return (
    lang === selected ||
    lang.startsWith(`${selected}-`) ||
    lang.startsWith(`${selectedBase}-`) ||
    lang.split("-")[0] === selectedBase
  );
}

/** Option label for the language chosen in the header. Falls back to option.text. */
function readGridspotOptionLabel(option, locale) {
  const translations = Array.isArray(option?.translations)
    ? option.translations
    : [];
  const match = translations.find((item) =>
    matchesLocale(item?.language || item?.locale, locale),
  );
  const translated = match?.text;
  if (translated != null && String(translated).trim()) {
    return stripHtml(translated);
  }
  return option?.text ? stripHtml(option.text) : "";
}

function normalizeTextPosition(value) {
  const key = String(value || "center_center")
    .trim()
    .toLowerCase()
    .replace(/centre/g, "center")
    .replace(/middle/g, "center")
    .replace(/[\s_]+/g, "-");
  return GRIDSPOT_TEXT_ALIGN[key] ? key : "center-center";
}

/** Style fields live on the option. Null or a missing field means that setting is off. */
function readOptionTextStyle(style) {
  if (!style || typeof style !== "object" || Array.isArray(style)) return null;

  const source = {};
  GRIDSPOT_TEXT_STYLE_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(style, key)) {
      source[key] = style[key];
    }
  });

  return {
    font: source.font == null || source.font === "" ? "system" : source.font,
    size: source.size == null || source.size === "" ? 4 : source.size,
    colour:
      source.colour == null || source.colour === "" ? "#FFFFFF" : source.colour,
    background:
      source.background == null || source.background === ""
        ? null
        : source.background,
    background_opacity:
      source.background_opacity == null || source.background_opacity === ""
        ? 1
        : source.background_opacity,
    position:
      source.position == null || source.position === ""
        ? "center_center"
        : source.position,
  };
}

const GRIDSPOT_FONT_FAMILIES = {
  system:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", "Helvetica Neue", Arial, sans-serif',
  sans: '"Noto Sans", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  serif: '"Noto Serif", Georgia, "Times New Roman", Times, serif',
  mono: 'ui-monospace, "Cascadia Code", "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
  rounded:
    '"Nunito", "SF Pro Rounded", "Segoe UI Rounded", system-ui, sans-serif',
};

function toCssFontFamily(font) {
  const raw = String(font || "").trim();
  const generic = GRIDSPOT_FONT_FAMILIES[raw.toLowerCase()];
  if (!raw || generic) {
    return generic || GRIDSPOT_FONT_FAMILIES.system;
  }
  if (raw.includes(",")) return raw;
  return /\s/.test(raw) ? `"${raw}", sans-serif` : `${raw}, sans-serif`;
}

/** Unitless size is a percentage of the image width so it scales with the photo. */
function toCssFontSize(size) {
  const raw = String(size).trim();
  if (/^\d+(\.\d+)?(px|rem|em|%|cqw|cqi)$/.test(raw)) return raw;
  if (/^\d+(\.\d+)?$/.test(raw)) return `${raw}cqw`;
  return "4cqw";
}

function toCssColor(value) {
  const raw = String(value).trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(raw)) return raw;
  if (/^([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(raw)) return `#${raw}`;
  if (/^(rgb|rgba|hsl|hsla)\(/i.test(raw)) return raw;
  if (/^[a-z]+$/i.test(raw)) return raw;
  return undefined;
}

function toCssBackground(color, opacity) {
  const cssColor = toCssColor(color);
  if (!cssColor) return undefined;
  const alpha = Number(opacity);
  const safeAlpha = Number.isFinite(alpha) ? Math.min(1, Math.max(0, alpha)) : 1;
  if (safeAlpha >= 1) return cssColor;

  if (cssColor.startsWith("#")) {
    const hex = cssColor.slice(1);
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map((char) => char + char)
            .join("")
        : hex;
    const red = parseInt(full.slice(0, 2), 16);
    const green = parseInt(full.slice(2, 4), 16);
    const blue = parseInt(full.slice(4, 6), 16);
    if ([red, green, blue].some((channel) => Number.isNaN(channel))) {
      return cssColor;
    }
    return `rgba(${red}, ${green}, ${blue}, ${safeAlpha})`;
  }

  const rgb = cssColor.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i,
  );
  if (rgb) {
    return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${safeAlpha})`;
  }

  const hsl = cssColor.match(
    /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%/i,
  );
  if (hsl) {
    return `hsla(${hsl[1]}, ${hsl[2]}%, ${hsl[3]}%, ${safeAlpha})`;
  }

  if (/^[a-z]+$/i.test(cssColor)) {
    return `color-mix(in srgb, ${cssColor} ${safeAlpha * 100}%, transparent)`;
  }

  return cssColor;
}

/** Bounding box of every cell the spot covers, as percentages of the image. */
function getGridspotCellsBoundsStyle(option, rows, columns) {
  const cells = getGridspotCells(option).filter(
    ({ row, column }) =>
      row >= 1 && row <= rows && column >= 1 && column <= columns,
  );
  if (!cells.length || rows < 1 || columns < 1) return null;

  const minRow = Math.min(...cells.map((cell) => cell.row));
  const maxRow = Math.max(...cells.map((cell) => cell.row));
  const minColumn = Math.min(...cells.map((cell) => cell.column));
  const maxColumn = Math.max(...cells.map((cell) => cell.column));

  return {
    left: `${((minColumn - 1) / columns) * 100}%`,
    top: `${((minRow - 1) / rows) * 100}%`,
    width: `${((maxColumn - minColumn + 1) / columns) * 100}%`,
    height: `${((maxRow - minRow + 1) / rows) * 100}%`,
  };
}

/**
 * Labels painted on the image when gridspot_text_overlay is on.
 * Each option uses only its own gridspot_text_style.
 * @returns {{key: string, text: string, boxStyle: object, labelStyle: object}[]}
 */
export function getGridspotTextOverlays(question, locale = selectedLocale()) {
  if (!isGridspotTextOverlayEnabled(question)) return [];

  const rows = getGridspotRows(question);
  const columns = getGridspotColumns(question);
  if (!rows || !columns) return [];

  return (question?.options || []).flatMap((option, index) => {
    const text = readGridspotOptionLabel(option, locale);
    const style = readOptionTextStyle(option?.gridspot_text_style);
    if (!text || !style) return [];
    const boxStyle = getGridspotCellsBoundsStyle(option, rows, columns);
    if (!boxStyle) return [];

    const align = GRIDSPOT_TEXT_ALIGN[normalizeTextPosition(style.position)];
    return [
      {
        key: String(option?._id ?? option?.order ?? index),
        text,
        boxStyle: {
          ...boxStyle,
          alignItems: align.alignItems,
          justifyContent: align.justifyContent,
        },
        labelStyle: {
          fontFamily: toCssFontFamily(style.font),
          fontSize: toCssFontSize(style.size),
          color: toCssColor(style.colour) || "#FFFFFF",
          textAlign: align.textAlign,
          backgroundColor: style.background
            ? toCssBackground(style.background, style.background_opacity)
            : undefined,
        },
      },
    ];
  });
}
